import clsx from "clsx";
import styled from "styled-components";
import { $champagne7, $white, $beigeBg } from "../../Utils/color";
import { IQuantifierProps } from "./Quantifier";
import { responsiveMobileBreakpoint } from "../../Components/Grid/_utils/responsiveObserve";
import { defaultTheme } from "../../Themes/defaultTheme";

interface IQuantifierRootProps extends IQuantifierProps {
  useMobileTemplate?: boolean;
  addOnly?: boolean;
  $loading?: boolean;
  $backgroundColor?: string;
  $size?: "small" | "medium";
  $variant?: "dark" | "light" | "outline";
}
export const QuantifierRoot = styled.div.attrs<IQuantifierRootProps>(
  ({ useMobileTemplate, addOnly, $loading, className, $size, ...props }) => {
    return {
      ...props,
      className: clsx(
        "ro-quantifier",
        useMobileTemplate && "ro-quantifier--mobile-alt",
        $loading && "ro-quantifier--loading",
        addOnly && "ro-quantifier--add-only",
        $size && "ro-quantifier--size-" + $size,
        className
      ),
    };
  }
)<IQuantifierRootProps>((props) => {
  const { useMobileTemplate, $variant } = props;
  const isOutline = $variant === "outline";
  const color = $white;
  const background = $champagne7;
  const backgroundMobile = $beigeBg;

  return {
    position: "relative",
    display: useMobileTemplate ? "none" : "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "36px",
    padding: isOutline ? 0 : "0 6px",
    gap: isOutline ? 8 : undefined,
    borderRadius: isOutline ? undefined : "48px",
    color: isOutline ? $champagne7 : color,
    background: isOutline ? "transparent" : background,
    userSelect: "none",
    ["span"]: {
      display: "inline-block",
      textAlign: "center",
      width: isOutline ? "22px" : "20px",
      padding: isOutline ? 0 : "0 2px",
      transform: isOutline ? undefined : "translateY(-1px)",
      ...(isOutline && {
        fontSize: "17px",
        lineHeight: 1,
        letterSpacing: "-0.17px",
      }),
    },
    ["&.ro-quantifier--size-small"]: {
      height: "30px",
      padding: isOutline ? 0 : "0 3px",
      [`@media ${responsiveMobileBreakpoint}`]: {
        height: "36px",
        padding: isOutline ? 0 : "0 6px",
      },
    },
    [`@media ${responsiveMobileBreakpoint}`]: {
      ["&.ro-quantifier--mobile-alt"]: {
        background: isOutline ? "transparent" : backgroundMobile,
        display: useMobileTemplate ? "none" : undefined,
      },
    },
    [".ro-typography"]: {
      transition: "opacity .25s, visibility .25s",
    },
    ["&.ro-quantifier--loading .ro-typography"]: {
      opacity: 0,
    },
  };
});
export const QuantifierMobileRoot = styled.div<{}>(() => {
  return {
    position: "relative",
    margin: "auto 0 auto auto",
    ["@media screen and (min-width: 768px)"]: {
      // display: "none",
    },
  };
});

export const QuantifierMobileButton = styled.button<{
  $addOnly?: boolean;
  $busy?: boolean;
  $variant?: "light" | "dark";
  $size?: "small" | "medium";
  $controls?: boolean;
  $backgroundColor?: string;
}>(({ $addOnly, $busy, $variant, $size, $controls, $backgroundColor }) => {
  const { color: themeColors } = defaultTheme;
  const { champagne7, beigeBg, white } = themeColors;
  const isDark = $variant === "dark";
  // Use the custom background color if provided, otherwise fallback to default colors based on theme
  const backgroundColor = $backgroundColor || (isDark ? "#494949" : beigeBg);
  const isSmall = $size === "small";
  const pxSize = isSmall ? 28 : 36;
  const color = isDark ? white : champagne7;
  const uiZoneOffset = 16;

  return {
    display: "flex",
    position: "relative",
    padding: "0",
    minWidth: pxSize,
    height: pxSize,
    margin: "auto 0 auto auto",
    border: "none",
    backgroundColor,
    borderRadius: pxSize,
    color,
    ["&.ro-quantifier--empty button"]: {
      backgroundColor: backgroundColor,
    },
    ["&.ro-quantifier__mobile-controls"]: {
      backgroundColor: backgroundColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 86,
      ".ro-typography": {
        display: "inline-block",
        padding: 0,
        margin: "0 -11px",
        ...(!!$busy && {
          opacity: 0,
          visibility: "hidden",
        }),
      },
      [".ro-quantifier__mobile-minus, .ro-quantifier__mobile-plus"]: {
        width: 36,
        height: 36,
      },
      ".ro-quantifier__mobile-plus": {
        ".ui-zone": {
          paddingLeft: 0,
          left: 0,
        },
      },
      ".ro-quantifier__mobile-minus": {
        ".ui-zone": {
          paddingRight: 0,
          right: 0,
        },
      },
    },
    ["+ select, .ui-zone"]: {
      width: "100%",
      height: "100%",
      position: "absolute",
      opacity: 0,
      padding: uiZoneOffset,
      top: -uiZoneOffset,
      left: -uiZoneOffset,
      boxSizing: "content-box",
    },
    [".ricon"]: {
      margin: !$controls ? "auto 8px auto -8px" : undefined,
      position: "relative",
      transition: "opacity .5s, visibility .5s, margin-left .5s",
    },
    [".ricon-Plus"]: {
      ...(!$addOnly &&
        !$controls && {
          position: "absolute",
          bottom: 0,
          top: 0,
          left: 0,
          right: 0,
          height: "1em",
          opacity: 0,
          visibility: "hidden",
          margin: "auto 0",
        }),
    },
    [".ro-typography"]: {
      position: "relative",
      transition: "opacity .25s, visibility .25s",
      margin: "auto",
      lineHeight: "1em",
      display: "inline-flex",
      padding: `0 8px ${isSmall ? 0.2 : 0.1}em 8px`,
      minWidth: pxSize,
      boxSizing: "border-box",
      textAlign: "center",
    },
    [".spinner"]: {
      position: "absolute",
      bottom: 0,
      top: 0,
      margin: "auto",
      left: 0,
      right: 0,
    },
    ["&[data-quantity='0']"]: {
      [".ro-typography"]: {
        opacity: 0,
        visibility: "hidden",
        margin: "auto -11px auto -11px",
        // ...($addOnly && { margin: "auto -11px auto -11px" }),
      },
      ...($addOnly
        ? {
            [".ricon-Plus"]: {
              margin: "auto -6px auto -8px",
            },
          }
        : {
            ["+ select"]: {
              display: "none",
            },
            [".ricon-Arrowhead_down"]: {
              marginLeft: -24,
              opacity: 0,
              visibility: "hidden",
            },
            [".ricon-Plus"]: {
              opacity: 1,
              visibility: "visible",
            },
          }),
    },
    ...(!!$busy &&
      !$controls && {
        [".ro-typography, .ricon, &[data-quantity='0'] .ricon-Plus"]: {
          opacity: 0,
          visibility: "hidden",
          margin: "auto 0",
        },
      }),
  };
});

export const QuantifierMobileDropdownRoot = styled.div<{
  open: boolean;
}>(({ open }) => {
  const { color: themeColors } = defaultTheme;
  const { champagne7, beigeBg, white } = themeColors;
  const backgroundColor = beigeBg;
  const color = champagne7;
  return {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "12px 16px",
    minWidth: 36,
    minHeight: 36,
    top: 80,
    right: -10,
    margin: "auto",
    border: "none",
    backgroundColor,
    borderRadius: 24,
    color,
    visibility: "hidden",
    opacity: 0,
    transition: "opacity .5s, visibility .5s",
    //zIndex: 1,
    ...(open && {
      visibility: "visible",
      opacity: 1,
    }),
    ["button"]: {
      border: "none",
      padding: "3px 0",
      gap: 6,
      margin: 0,
      display: "flex",
      background: "none",
      height: 17,
    },
  };
});

export const QuantifierButton = styled.button<{
  $variant?: "light" | "dark" | "outline";
}>(({ disabled, $variant }) => {
  const { color: themeColors } = defaultTheme;
  const { champagne } = themeColors;
  const isOutline = $variant === "outline";

  if (isOutline) {
    return {
      position: "relative",
      display: "flex",
      width: "36px",
      height: "36px",
      alignItems: "center",
      justifyContent: "center",
      background: $white,
      borderRadius: "50%",
      border: "none",
      boxShadow: "none",
      opacity: disabled ? 0.3 : 1,
      pointerEvents: disabled ? "none" : "inherit",
      padding: 0,
      cursor: "pointer",
    };
  }

  return {
    position: "relative",
    display: "flex",
    width: "24px",
    height: "24px",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    opacity: disabled ? 0.3 : 1,
    pointerEvents: disabled ? "none" : "inherit",
    padding: 0,
    cursor: "pointer",
    ["&:before"]: {
      [`@media ${responsiveMobileBreakpoint}`]: {
        background: "transparent",
      },
      //zIndex: 0,
      position: "absolute",
      content: "''",
      borderRadius: "48px",
      width: 24,
      height: 24,
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    ["i"]: {
      position: "relative",
    },
    ["&:hover"]: {
      i: {
        color: $variant === "light" ? "#fff" : undefined,
      },
      ["&:before"]: {
        background: $variant === "light" ? champagne : "#000",
      },
    },
  };
});

/**
 * Reproduit au pixel près les traits +/- du Figma "Accompagnement"
 * (ligne 1px de 13px de long, cf. asset SVG "Ajouter panier").
 */
export const QuantifierOutlineIcon = styled.span<{
  $icon: "plus" | "minus";
}>(({ $icon }) => ({
  position: "relative",
  display: "block",
  width: 13,
  height: 13,
  ["&:before, &:after"]: {
    content: "''",
    position: "absolute",
    background: $champagne7,
    left: "50%",
    top: "50%",
  },
  ["&:before"]: {
    width: 13,
    height: 1,
    transform: "translate(-50%, -50%)",
  },
  ["&:after"]: {
    width: $icon === "plus" ? 1 : 0,
    height: $icon === "plus" ? 13 : 0,
    transform: "translate(-50%, -50%)",
  },
}));

export const QuantifierAdd = styled.button<{ $loading?: boolean }>(
  ({ disabled, $loading }) => ({
    position: "relative",
    display: "flex",
    height: "24px",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    opacity: disabled ? 0.3 : 1,
    pointerEvents: disabled ? "none" : "inherit",
    color: "#fff",
    padding: "0 17px",
    cursor: "pointer",
    ["span"]: {
      width: "auto",
    },
    ...($loading && {
      [".ro-typography"]: {
        opacity: 0,
      },
    }),
  })
);
